/**
 * Infrastructure presentation adapter for Operational Readiness.
 * Maps bridges / cameras / API-key checklist signals — no business rules.
 *
 * Phase 18 Infrastructure order (organization prerequisites first):
 *   API Key → Bridge Created → Bridge Connected → Bridge Credentials → Cameras
 *
 * Bridge Connected uses organization-level EEN Device Health (all mapped
 * bridges Online). It does not use Bridge.last_sync_* or bridge-access sync.
 */

import {OperationalStatusCardView, OperationalWarningView,} from '../operational-readiness.models';
import {OnboardingChecklistItem} from '../../organization-onboarding/organization-onboarding.types';
import {
    BridgeReadinessRow,
    CameraReadinessRow,
    DeviceHealthReadinessPayload,
    DeviceHealthReadinessRow,
} from './shared';

export type {BridgeReadinessRow, CameraReadinessRow} from './shared';
export type {DeviceHealthReadinessPayload, DeviceHealthReadinessRow} from './shared';

export type InfrastructureAdapterInput = {
    bridges: BridgeReadinessRow[] | null;
    cameras: CameraReadinessRow[] | null;
    checklistItems: OnboardingChecklistItem[];
    /**
     * null = Device Health request failed / could not be loaded.
     * Payload with status !== 'available' = snapshot unavailable.
     */
    deviceHealth: DeviceHealthReadinessPayload | null;
};

const DEVICE_TYPE_BRIDGE = 'bridge';
const MAPPING_MAPPED = 'mapped';
const MAPPING_NOT_FOUND = 'not_found';
const CONNECTION_ONLINE = 'Online';

function checklistItemByKey(
    items: OnboardingChecklistItem[],
    key: string,
): OnboardingChecklistItem | undefined {
    return items.find((item) => item.key === key);
}

function readyCard(
    key: string,
    title: string,
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'ready',
        statusLabel: 'Ready',
        detail,
    };
}

function pendingCard(
    key: string,
    title: string,
    warning: OperationalWarningView | undefined,
    status: 'missing' | 'blocked' | 'warning' = 'missing',
    statusLabel = 'Pending',
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status,
        statusLabel,
        detail,
        warnings: warning ? [warning] : undefined,
    };
}

function unknownCard(key: string, title: string, detail: string): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'unknown',
        statusLabel: 'Unknown',
        detail,
    };
}

function cameraReadyDetail(cameras: CameraReadinessRow[]): string {
    const activeCount = cameras.filter((camera) => camera.is_active !== false).length;
    const esns = cameras
        .map((camera) => (camera.camera_esn || '').trim())
        .filter(Boolean)
        .slice(0, 3);
    const esnSuffix = esns.length
        ? ` · ESN: ${esns.join(', ')}${cameras.length > esns.length ? ', …' : ''}`
        : '';
    return (
        `${cameras.length} camera(s) registered` +
        (activeCount !== cameras.length ? ` (${activeCount} active)` : '') +
        esnSuffix
    );
}

function mapApiKeyCard(checklistItems: OnboardingChecklistItem[]): OperationalStatusCardView {
    const apiKeyItem = checklistItemByKey(checklistItems, 'api_key_generated');
    if (apiKeyItem?.completed) {
        return readyCard(
            'api_key',
            'API Key',
            'Active webhook API key exists for this organization.',
        );
    }
    return pendingCard('api_key', 'API Key', {
        problem: 'No active webhook API key exists for this organization.',
        impact: 'API-key authenticated LPR webhooks will be rejected.',
        recommendedAction:
            'Generate an active webhook API key for this organization (Admin → API Keys).',
        scope: 'organization',
        condition: 'Organization-level prerequisite before camera integrations that use API keys',
    });
}

function mapCamerasCard(
    cameras: CameraReadinessRow[] | null,
    checklistItems: OnboardingChecklistItem[],
): OperationalStatusCardView {
    if (cameras === null) {
        const checklistCamera = checklistItemByKey(checklistItems, 'camera_added');
        if (checklistCamera?.completed) {
            return readyCard('cameras_configured', 'Cameras Configured');
        }
        if (checklistCamera) {
            return pendingCard('cameras_configured', 'Cameras Configured', {
                problem: 'No camera is registered for this organization.',
                impact: 'Vehicle detections cannot be mapped to this organization.',
                recommendedAction:
                    'Register an Eagle Eye camera using its ESN (Add Camera).',
                scope: 'organization',
                condition: 'Requires at least one camera with its Eagle Eye ESN',
            });
        }
        return unknownCard(
            'cameras_configured',
            'Cameras Configured',
            'Camera list could not be loaded.',
        );
    }
    if (cameras.length > 0) {
        return readyCard('cameras_configured', 'Cameras Configured', cameraReadyDetail(cameras));
    }
    return pendingCard('cameras_configured', 'Cameras Configured', {
        problem: 'No camera is registered for this organization.',
        impact: 'Vehicle detections cannot be mapped to this organization.',
        recommendedAction: 'Register an Eagle Eye camera using its ESN (Add Camera).',
        scope: 'organization',
        condition: 'Requires at least one camera with its Eagle Eye ESN',
    });
}

function isBridgeDevice(row: DeviceHealthReadinessRow): boolean {
    return String(row.device_type || '').toLowerCase() === DEVICE_TYPE_BRIDGE;
}

function isMappedBridge(row: DeviceHealthReadinessRow): boolean {
    return String(row.mapping_status || '').toLowerCase() === MAPPING_MAPPED;
}

function isNotFoundBridge(row: DeviceHealthReadinessRow): boolean {
    return String(row.mapping_status || '').toLowerCase() === MAPPING_NOT_FOUND;
}

function isOnlineConnection(row: DeviceHealthReadinessRow): boolean {
    return String(row.connection_status || '').trim() === CONNECTION_ONLINE;
}

function deviceHealthAvailable(payload: DeviceHealthReadinessPayload): boolean {
    return String(payload.status || '').toLowerCase() === 'available';
}

/**
 * Organization Bridge Connected — all mapped org bridges Online via Device Health.
 * last_sync_* / bridge-access sync are intentionally ignored.
 */
function mapBridgeConnectedCard(
    bridges: BridgeReadinessRow[] | null,
    deviceHealth: DeviceHealthReadinessPayload | null,
): OperationalStatusCardView {
    if (bridges === null) {
        return unknownCard(
            'bridge_connected',
            'Bridge Connected',
            'Bridge list could not be loaded to evaluate connectivity.',
        );
    }

    if (bridges.length === 0) {
        return pendingCard('bridge_connected', 'Bridge Connected', {
            problem: 'Bridge connectivity cannot be confirmed because no bridge exists.',
            impact: 'Organization Bridge connectivity is unavailable until a bridge is created.',
            recommendedAction: 'Create a bridge for this organization, then confirm Device Health.',
            scope: 'organization',
            condition: 'Requires Bridge Created',
        });
    }

    if (deviceHealth === null) {
        return unknownCard(
            'bridge_connected',
            'Bridge Connected',
            'Device Health could not be loaded.',
        );
    }

    if (!deviceHealthAvailable(deviceHealth)) {
        return unknownCard(
            'bridge_connected',
            'Bridge Connected',
            deviceHealth.detail
                ? `Device Health is unavailable: ${deviceHealth.detail}`
                : 'Device Health snapshot is not available.',
        );
    }

    const results = Array.isArray(deviceHealth.results) ? deviceHealth.results : [];
    // Org-scoped API may still include unmapped rows when unfiltered; never attribute them.
    const bridgeRows = results.filter(isBridgeDevice);
    const mappedBridges = bridgeRows.filter(isMappedBridge);
    const notFoundBridges = bridgeRows.filter(isNotFoundBridge);

    if (mappedBridges.length === 0) {
        return pendingCard(
            'bridge_connected',
            'Bridge Connected',
            {
                problem:
                    notFoundBridges.length > 0
                        ? 'Configured bridges were not found in the current Device Health inventory.'
                        : 'No mapped Eagle Eye bridges were found for this organization in Device Health.',
                impact: 'Organization Bridge connectivity cannot be confirmed as Online.',
                recommendedAction:
                    'Verify each bridge ESN matches Eagle Eye, then refresh Device Health.',
                scope: 'organization',
            },
            'warning',
            'Warning',
        );
    }

    if (notFoundBridges.length > 0) {
        return pendingCard(
            'bridge_connected',
            'Bridge Connected',
            {
                problem:
                    `${notFoundBridges.length} configured bridge(s) were not found in Eagle Eye Device Health.`,
                impact: 'Organization Bridge Connected requires every configured bridge to be Online.',
                recommendedAction:
                    'Correct bridge ESN mapping or restore the missing Eagle Eye bridge, then refresh Device Health.',
                scope: 'organization',
            },
            'warning',
            'Warning',
            `${mappedBridges.length} mapped · ${notFoundBridges.length} not found`,
        );
    }

    // Configured KYRA bridges must not silently disappear from the calculation.
    const healthBridgeIds = new Set(
        mappedBridges
            .map((row) => (row.kyra_bridge_id || '').trim())
            .filter(Boolean),
    );
    const missingFromHealth = bridges.filter((bridge) => {
        const id = (bridge.id || '').trim();
        return !!id && !healthBridgeIds.has(id);
    });
    if (missingFromHealth.length > 0) {
        return pendingCard(
            'bridge_connected',
            'Bridge Connected',
            {
                problem:
                    `${missingFromHealth.length} configured bridge(s) are missing from the Device Health snapshot.`,
                impact: 'Organization Bridge Connected cannot be Ready until every configured bridge is confirmed Online.',
                recommendedAction:
                    'Verify bridge ESNs and refresh Device Health so every KYRA bridge is mapped.',
                scope: 'organization',
            },
            'warning',
            'Warning',
        );
    }

    const offlineOrOther = mappedBridges.filter((row) => !isOnlineConnection(row));
    if (offlineOrOther.length > 0) {
        return pendingCard(
            'bridge_connected',
            'Bridge Connected',
            {
                problem:
                    `${offlineOrOther.length} of ${mappedBridges.length} organization bridge(s) are not Online in Device Health.`,
                impact: 'Organization Bridge Connected requires every configured bridge to be Online.',
                recommendedAction:
                    'Restore connectivity for offline or degraded bridges, then refresh Device Health.',
                scope: 'organization',
            },
            'warning',
            'Warning',
            `${mappedBridges.length - offlineOrOther.length} of ${mappedBridges.length} Online`,
        );
    }

    return readyCard(
        'bridge_connected',
        'Bridge Connected',
        `${mappedBridges.length} organization bridge(s) Online`,
    );
}

export function mapInfrastructureCards(
    input: InfrastructureAdapterInput,
): OperationalStatusCardView[] {
    const {bridges, cameras, checklistItems, deviceHealth} = input;
    const cards: OperationalStatusCardView[] = [];

    // 1. API Key — organization-level prerequisite (early in Infrastructure)
    cards.push(mapApiKeyCard(checklistItems));

    // 2. Bridge Created
    if (bridges === null) {
        cards.push(
            unknownCard(
                'bridge_created',
                'Bridge Created',
                'Bridge list could not be loaded.',
            ),
        );
    } else if (bridges.length > 0) {
        const activeCount = bridges.filter((bridge) => bridge.is_active !== false).length;
        cards.push(
            readyCard(
                'bridge_created',
                'Bridge Created',
                `${bridges.length} bridge(s) registered` +
                (activeCount !== bridges.length ? ` (${activeCount} active)` : ''),
            ),
        );
    } else {
        cards.push(
            pendingCard('bridge_created', 'Bridge Created', {
                problem: 'No bridge is registered for this organization.',
                impact: 'Bridge sync and bridge credential workflows are unavailable.',
                recommendedAction: 'Create a bridge for this organization.',
                scope: 'organization',
            }),
        );
    }

    // 3. Bridge Connected — org-level EEN Device Health (all mapped bridges Online)
    cards.push(mapBridgeConnectedCard(bridges, deviceHealth));

    // 4. Bridge Credentials
    if (bridges === null) {
        const checklistBridge = checklistItemByKey(checklistItems, 'bridge_configured');
        if (checklistBridge?.completed) {
            cards.push(readyCard('bridge_credentials', 'Bridge Credentials'));
        } else if (checklistBridge) {
            cards.push(
                pendingCard('bridge_credentials', 'Bridge Credentials', {
                    problem: 'No active bridge access credential is available.',
                    impact: 'Bridge sync and bridge health checks cannot authenticate for this organization.',
                    recommendedAction: 'Issue a bridge access credential from Bridge Admin.',
                    scope: 'organization',
                }),
            );
        } else {
            cards.push(
                unknownCard(
                    'bridge_credentials',
                    'Bridge Credentials',
                    'Bridge credential status could not be loaded.',
                ),
            );
        }
    } else if (bridges.length === 0) {
        cards.push(
            pendingCard('bridge_credentials', 'Bridge Credentials', {
                problem: 'Bridge credentials cannot exist until a bridge is created.',
                impact: 'Bridge sync cannot authenticate.',
                recommendedAction: 'Create a bridge, then issue a bridge access credential.',
                scope: 'organization',
            }),
        );
    } else {
        const withCredential = bridges.filter((bridge) => !!bridge.primary_masked_credential);
        if (withCredential.length > 0) {
            cards.push(
                readyCard(
                    'bridge_credentials',
                    'Bridge Credentials',
                    `${withCredential.length} bridge(s) with an active credential`,
                ),
            );
        } else {
            cards.push(
                pendingCard('bridge_credentials', 'Bridge Credentials', {
                    problem: 'No active bridge access credential is available.',
                    impact: 'Bridge sync and bridge health checks cannot authenticate for this organization.',
                    recommendedAction: 'Issue a bridge access credential from Bridge Admin.',
                    scope: 'organization',
                }),
            );
        }
    }

    // 5. Cameras Configured — registration identity (ESN), not analytics readiness
    cards.push(mapCamerasCard(cameras, checklistItems));

    return cards;
}
